import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { getLoopingIndex } from "../../../../utils/arrays-helper.ts";
import "../../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../../assets/fa-icons/css/sharp-solid.css";
import LoadingResponsiveImage from "../../../../components/responsive-image/loading-responsive-image.tsx";
import { screenWidths } from "../../../../utils/screen-widths.ts";
import { FloatingHeroItem } from "../../components/floating-hero-item.tsx";
import { cn } from "../../../../utils/cn.ts";
import { focusStylesAfter } from "../../../../utils/focus-styles.ts";
import { ArrowButton } from "../../../../components/buttons/arrow-button.tsx";
import { usePropertyDetailsContext } from "../../../contexts/property-details-context.tsx";

export const PropertyImageGallery: React.FC = () => {
  const { images = [] } = usePropertyDetailsContext();
  const [currentSlide, setCurrentSlide] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const nextPhotos = [1, 2, 3, 4, 5].map((offset) => {
    const index = getLoopingIndex(images, currentSlide + offset);
    return { index, image: images[index] };
  });
  const prevPhoto = images[getLoopingIndex(images, currentSlide - 1)];
  const showingCarousel = images.length >= 2;
  const showingSideImages = images.length >= 3;
  // The side column is a third of the hero minus the gap: ~394px at the 1200px design width,
  // narrower on tablets. Declaring it accurately lets a 2x screen pick the 800w candidate.
  const thumbnailDesktopWidth = "min(33vw, 400px)";

  useEffect(() => {
    const preloadImages = [
      { image: nextPhotos[2].image, mobileWidth: "0px", desktopWidth: thumbnailDesktopWidth },
      { image: nextPhotos[3].image, mobileWidth: "0px", desktopWidth: thumbnailDesktopWidth },
      { image: nextPhotos[4].image, mobileWidth: "0px", desktopWidth: thumbnailDesktopWidth },
      { image: prevPhoto, mobileWidth: "100vw", desktopWidth: "800px" },
    ];

    const links = preloadImages.map(({ image, mobileWidth, desktopWidth }) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.imageSrcset = image.sources.map((s) => `${s.url} ${s.width.toString()}w`).join(", ");
      link.imageSizes = `(max-width: ${screenWidths.md}) ${mobileWidth}, ${desktopWidth}`;
      document.head.appendChild(link);
      return link;
    });

    return () => {
      links.forEach((link) => link.remove());
    };
  }, [currentSlide]);

  const goToSlide = (offset: number) => {
    swiperRef.current?.slideToLoop((currentSlide + offset) % images.length);
  };

  return (
    <div className="flex gap-4 md:gap-4.5 w-full h-full">
      <div className="flex-[2] min-w-0 relative">
        {showingCarousel && (
          <>
            <Swiper
              slidesPerView={1}
              spaceBetween={0}
              loop={true}
              className="w-full h-full"
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                setCurrentSlide(swiper.realIndex);
              }}
              onSlideChange={(swiper) => setCurrentSlide(swiper.realIndex)}
            >
              {useMemo(() => {
                return images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <LoadingResponsiveImage
                      image={image}
                      className="w-full h-full"
                      desktopWidth="800px"
                      eagerLoading={index === 0}
                    />
                  </SwiperSlide>
                ));
              }, [images])}
            </Swiper>
            {/* Prev/next buttons */}
            <div className="absolute inset-0 flex items-center justify-between px-2.5 md:px-5 pointer-events-none z-10">
              <ArrowButton
                direction="prev"
                colourStyle="dark"
                ariaLabel="Previous image"
                onClick={() => swiperRef.current?.slidePrev()}
              />
              <ArrowButton
                direction="next"
                colourStyle="dark"
                ariaLabel="Next image"
                onClick={() => swiperRef.current?.slideNext()}
              />
            </div>
            {showingSideImages && (
              <div className="absolute bottom-2.5 right-2.5 md:right-5 md:bottom-5 z-10 hidden md:block">
                <FloatingHeroItem
                  label={`${(currentSlide + 1).toString()} / ${images.length.toString()}`}
                  iconName="images"
                />
              </div>
            )}
          </>
        )}
        {!showingCarousel && <LoadingResponsiveImage image={images[0]} className="w-full h-full" eagerLoading />}
      </div>
      {showingSideImages && (
        <div className="flex-[1]   flex-col gap-4 md:gap-4.5 hidden md:flex">
          {nextPhotos.slice(0, 2).map((photo, i) => (
            <button
              key={i}
              type="button"
              className={cn("flex-1 min-h-0 cursor-pointer relative", focusStylesAfter)}
              onClick={() => goToSlide(i + 1)}
              aria-label={`Go to Slide ${(photo.index + 1).toString()}`}
            >
              <LoadingResponsiveImage
                key={photo.image.sources[0]?.url}
                image={photo.image}
                className="w-full h-full"
                mobileWidth="0px"
                eagerLoading
                desktopWidth={thumbnailDesktopWidth}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
